import { Controller } from "@hotwired/stimulus"
import { readLabelText } from "../../utils/label-text.util"
import { diffRegisterRecord } from "../../utils/register-diff.util"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  driftTitle: "",
  matchTitle: "",
  matchHint: ""
}

// Edit mode compares; new-record mode searches — this controller only ever
// renders for a persisted contact (see _contact_modal_content.html.erb). On
// connect it fetches the register record for the party's stored IČO and shows
// either what changed since the party was saved, or an explicit "matches"
// confirmation. The calm state matters as much as the warning: without it the
// user cannot tell whether the check ran at all, and a warning that only ever
// appears in isolation reads as noise rather than signal.
//
// A failed/unreachable check must never block editing — it shows nothing and
// leaves the form exactly as it was, the same contract legal_entity_controller
// already has for its own registry_unavailable path (no second notice here).
export default class extends Controller {
  static targets = ["panel", "icon", "title", "list", "hint", "applyButton"]
  static values = {
    translations: { type: Object, default: {} },
    registrationId: String,
    countryId: String
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.diffs = []
    // The check is issued for exactly one register — the one belonging to
    // countryIdValue. Everything derived from its response (the listed diffs,
    // and the values apply() writes) is only valid while the form still shows
    // that country.
    this.checkSequence = 0
    this.abortController = null
    this.onCountryChanged = () => this.#invalidate()
    document.addEventListener("registry:country-changed", this.onCountryChanged)
    if (this.registrationIdValue && this.countryIdValue) this.check()
  }

  disconnect() {
    document.removeEventListener("registry:country-changed", this.onCountryChanged)
    this.#invalidatePendingCheck()
  }

  async check() {
    const url = `/legal_entity_registry/search?country_id=${encodeURIComponent(this.countryIdValue)}&search_type=legal_registration_id&search_value=${encodeURIComponent(this.registrationIdValue)}`

    this.abortController = new AbortController()
    const request = this.checkSequence

    try {
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { Accept: "application/json" }
      })
      if (request !== this.checkSequence) return
      // Covers both a plain failure and the 503 "registry unavailable" the
      // server uses to distinguish an outage from "no such company" — either
      // way, show nothing rather than guess.
      if (!response.ok) return

      const data = await response.json()
      if (request !== this.checkSequence) return

      const register = Array.isArray(data) ? data[0] : data
      if (!register) return

      this.diffs = diffRegisterRecord({
        register,
        company: this.#currentValues(),
        referenceDate: new Date(),
        labels: this.#labels()
      })
      this.#render()
    } catch (_error) {
      // Network/parse failure: never block editing on it.
    }
  }

  apply() {
    for (const diff of this.diffs) {
      const input = document.getElementById(diff.inputId)
      // A registry refresh proposes name / address / DIČ / IČ DPH only — never
      // the IČO, which is the key the lookup was made on. Every one of those is
      // VERSIONED identity, so applying it to a locked party is safe: issued
      // documents keep their own pinned snapshot. This used to skip a `readonly`
      // input, but the identity keys are no longer rendered read-only — a
      // read-only field cannot tell a typo from a genuinely different company,
      // so re-pointing is a confirmation prompt on save (PartyEditor), not a
      // field the user is locked out of.
      if (!input) {
        continue
      }

      input.value = diff.register
      // address_name's `input` action list also includes `legal-entity#search`
      // (shared/registry/_search.html.erb) — the live company autocomplete.
      // Dispatching a plain `input` there would debounce a second, unrelated
      // registry search and pop the dropdown open with nothing to dismiss it
      // (legal_entity_controller has no blur/click-outside handler). The
      // controller's own #populateFields hits this exact problem when it
      // fills the same field from a real registry pick, and solves it the
      // same way: dispatch `change`, not `input` — that still reaches
      // `change->logo#loadForCompanyName` (so the logo guess still refreshes
      // when the name changes) without ever reaching `legal-entity#search`
      // (bound only to `input`). Every other diffed field's sole `input`
      // listener is `contacts--provenance#clear`, so those keep using `input`.
      const eventType = diff.inputId === "address_name" ? "change" : "input"
      input.dispatchEvent(new Event(eventType, { bubbles: true }))
    }

    // Every diff is applied now: nothing is held back, so the panel returns to
    // its calm state. (It used to keep locked identity keys listed as
    // unresolved, back when those inputs were read-only.)
    this.diffs = []
    this.#render()
  }

  // The country decider selects WHICH register answers. Change it and this
  // panel is describing the previous one: the rows it lists, and the values
  // apply() would write into the form, were fetched from a register that no
  // longer has anything to do with the company being edited — and
  // Contacts::ContactUpdater → Identity::PartyEditor persists them as a new
  // identity version, so the mistake outlives the modal.
  //
  // Clear rather than re-check against the newly chosen country: a registration
  // number is only unique WITHIN one register (SK and CZ both issue 8-digit
  // IČO), so re-running the stored IČO against the new country can return an
  // unrelated legal entity that merely shares the number — and offer its name
  // and address for Apply, which is a worse failure than the one this fixes.
  // The comparison belongs to the saved country and comes back with the next
  // render of the saved record.
  #invalidate() {
    this.#invalidatePendingCheck()
    this.diffs = []
    this.listTarget.textContent = ""
    this.applyButtonTarget.classList.add("d-none")
    // Hidden, not re-rendered: #render()'s empty state is the "matches the
    // register" confirmation, and claiming a match against a register we just
    // stopped trusting is the same lie in a calmer colour.
    this.panelTarget.classList.add("d-none")
  }

  // Cancels the check in flight and makes any response still on the wire stale,
  // so it can never be rendered or applied.
  #invalidatePendingCheck() {
    this.abortController?.abort()
    this.abortController = null
    this.checkSequence += 1
  }

  #render() {
    const drifted = this.diffs.length > 0

    this.panelTarget.classList.remove("d-none")
    this.panelTarget.classList.toggle("alert--warning", drifted)
    this.panelTarget.classList.toggle("alert--success", !drifted)
    this.iconTarget.textContent = drifted ? "warning" : "check_circle"
    this.titleTarget.textContent = drifted ? this.#texts.driftTitle : this.#texts.matchTitle
    this.applyButtonTarget.classList.toggle("d-none", !drifted)

    if (this.hasHintTarget) {
      this.hintTarget.classList.toggle("d-none", drifted)
      this.hintTarget.textContent = drifted ? "" : this.#texts.matchHint
    }

    // Never innerHTML here: register/company values are unescaped strings
    // from an external service — textContent is what keeps them inert.
    this.listTarget.textContent = ""
    for (const diff of this.diffs) {
      const li = document.createElement("li")
      const strike = document.createElement("s")
      strike.textContent = diff.current || ""
      const next = document.createElement("strong")
      next.textContent = diff.register || ""
      li.append(`${diff.label}: `, strike, " → ", next)
      this.listTarget.appendChild(li)
    }
  }

  #currentValues() {
    const read = (id) => document.getElementById(id)?.value || ""
    return {
      name: read("address_name"),
      street: read("address_street"),
      city: read("address_city"),
      postal_code: read("address_postal_code"),
      tax_id: read("party_tax_id"),
      vat_id: read("party_vat_id")
    }
  }

  #labels() {
    // readLabelText, not `textContent`: every one of these six labels now also
    // holds a provenance marker whose icon span contains a Material Symbols
    // LIGATURE. `textContent` reads through into it, so the drift rows rendered
    // as "Ulica account_balance: Zámocká 30 → Karpatská 8".
    const labelText = (id) => readLabelText(document.querySelector(`label[for='${id}']`))
    return {
      name: labelText("address_name"),
      street: labelText("address_street"),
      city: labelText("address_city"),
      postalCode: labelText("address_postal_code"),
      taxId: labelText("party_tax_id"),
      vatId: labelText("party_vat_id")
    }
  }
}
